/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    FileText,
    Download,
    Package,
    Save,
    Loader2,
    Calculator,
    Eye
} from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../utils/AuthContext';
import { listCustomers } from '../utils/db/customers';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import { listBookingsForRange } from '../utils/db/bookings';
import { getNextInvoiceNo, createInvoice, getInvoice, getInvoiceItems, updateInvoice } from '../utils/db/invoices';
import NonGstInvoiceLayout from '../components/NonGstInvoiceLayout';
import { getProfile } from '../utils/db/profiles';
import { computeInvoiceCharges, sanitizePercentInput } from '../utils/money';

const NonGstInvoiceGenerator = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const editInvoiceId = searchParams.get('edit');
    const [customers, setCustomers] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [selectedBookings, setSelectedBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [nextInvoiceNo, setNextInvoiceNo] = useState('001');
    const [isEditMode, setIsEditMode] = useState(false);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [franchiseProfile, setFranchiseProfile] = useState(null);

    const [showPreview, setShowPreview] = useState(false);
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [lastPreviewPayload, setLastPreviewPayload] = useState(null);
    const printComponentRef = useRef();

    const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm();

    useEffect(() => {
        if (user) {
            loadData();
            loadFranchiseProfile();
        }
    }, [user, editInvoiceId]);

    const toThreeDigit = (val) => {
        const n = parseInt(String(val), 10);
        return (Number.isFinite(n) ? n : 1).toString().padStart(3, '0');
    };

    const loadData = async () => {
        try {
            // Pass true for isNonGst flag to getNextInvoiceNo
            const [custs, nextNo] = await Promise.all([
                listCustomers(user.id),
                getNextInvoiceNo(user.id, true)
            ]);
            setCustomers(custs);

            if (editInvoiceId) {
                setIsEditMode(true);
                const invoice = await getInvoice(user.id, editInvoiceId);
                const items = await getInvoiceItems(user.id, editInvoiceId);

                if (invoice) {
                    const invNo = invoice.invoice_no ? (invoice.invoice_no.split('NG-')[1] || invoice.invoice_no) : ''; // Remove NG- prefix for display in edit maybe? Or keep it? 
                    // Actually, let's keep the numeric part for the input field if the user wants to edit the number.
                    // But getNextInvoiceNo returns string like "NG-002".
                    // The input field handles the prefix locally or we show it. 
                    // Let's assume we show the numeric part in the input, and the prefix 'NG-' is static text.

                    let numericPart = '';
                    if (invoice.invoice_no && invoice.invoice_no.includes('NG-')) {
                        numericPart = invoice.invoice_no.split('NG-')[1]; // e.g., '001'
                    } else {
                        // fallback
                        const match = invoice.invoice_no.match(/(\d+)$/);
                        numericPart = match ? match[0] : '';
                    }

                    const formData = {
                        invoiceNo: numericPart,
                        customerId: invoice.customer_id,
                        invoiceDate: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '',
                        fromDate: invoice.from_date ? new Date(invoice.from_date).toISOString().split('T')[0] : '',
                        toDate: invoice.to_date ? new Date(invoice.to_date).toISOString().split('T')[0] : '',
                        fuelCharge: invoice.fuel_percent || '',
                        royaltyCharge: invoice.royalty_percent || '',
                        docketCharge: invoice.docket_charge || '',
                        odaCharge: invoice.oda_charge || '',
                        otherCharge: invoice.other_charge || '',
                        // No user gst percent for non-GST invoice
                    };

                    reset(formData);
                    setNextInvoiceNo(numericPart); // Just the number
                    setInvoiceItems(items);

                    const bookingsData = await listBookingsForRange(
                        user.id,
                        invoice.customer_id,
                        invoice.from_date,
                        invoice.to_date
                    );

                    setBookings(bookingsData);
                    setFilteredBookings(bookingsData);

                    const bookingIds = items
                        .filter(item => item.booking_id)
                        .map(item => item.booking_id);

                    setSelectedBookings(bookingIds);
                }
            } else {
                // nextNo comes as "NG-00X". We want just "00X" for the input field
                const numPart = nextNo.replace('NG-', '');
                setNextInvoiceNo(numPart);
                try {
                    setValue('invoiceNo', numPart, { shouldValidate: true });
                } catch (e) { }
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to load initial data');
        }
    };

    const loadFranchiseProfile = async () => {
        try {
            const profile = await getProfile(user.id);
            if (profile) {
                // Transform database fields to match the expected format for InvoiceLayout
                // Similar to how InvoiceGenerator.js handles it
                setFranchiseProfile({
                    franchiseName: profile.franchise_name || 'TATYS',
                    address: profile.address || 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
                    mobileNo: profile.mobile_no || '8015695652',
                    email: profile.email || 'thirumazhisai.dist@gmail.com',
                    gstNo: profile.gst_no || '27CIOPP1874N1Z',
                    panNo: profile.pan_no || 'CIOPP1874N',
                    bankName: profile.bank_name || 'Canara Bank',
                    accountNo: profile.account_no || '102000820462',
                    ifsc: profile.ifsc || 'CNRB0003072',
                    branchName: profile.branch_name || 'THIRUMAZHISAI',
                    terms: profile.terms ? (typeof profile.terms === 'string' ? profile.terms.split('\n') : profile.terms) : [
                        'Payment should be made by cheque in favour of TATYS',
                        'Any queries must be forwarded within 15 days of receipt',
                        '18% interest will be charged on overdue bills'
                    ],
                    invoicePrefix: 'NG-',
                    logoUrl: profile.logo_url || null,
                    hsn_sac: profile.hsn_sac || null
                });
            }
        } catch (error) {
            console.error('Error loading profile:', error);
        }
    };

    const selectedCustomerId = watch('customerId');
    const fromDate = watch('fromDate');
    const toDate = watch('toDate');

    useEffect(() => {
        const run = async () => {
            if (user && selectedCustomerId && fromDate && toDate) {
                try {
                    const rows = await listBookingsForRange(user.id, selectedCustomerId, fromDate, toDate);
                    setBookings(rows);
                    setFilteredBookings(rows);
                    if (!isEditMode) {
                        setSelectedBookings(rows.map(b => b.id));
                    }
                } catch (e) {
                    toast.error('Failed to load bookings');
                }
            } else {
                setFilteredBookings([]);
                setSelectedBookings([]);
            }
        };
        run();
    }, [user, selectedCustomerId, fromDate, toDate]);

    const toggleBookingSelection = (bookingId) => {
        setSelectedBookings(prev => {
            if (prev.includes(bookingId)) {
                return prev.filter(id => id !== bookingId);
            } else {
                return [...prev, bookingId];
            }
        });
    };

    const selectAllBookings = () => {
        setSelectedBookings(filteredBookings.map(booking => booking.id));
    };

    const clearSelection = () => {
        setSelectedBookings([]);
    };

    const calculateConsignmentAmount = () => {
        return selectedBookings.reduce((sum, bookingId) => {
            const booking = filteredBookings.find(b => b.id === bookingId);
            const amount = parseFloat(booking?.amount) || 0;
            const otherCharge = parseFloat(booking?.other_charge) || 0;
            return sum + amount + otherCharge;
        }, 0);
    };

    const calculateTaxableBase = () => {
        const consignmentAmount = calculateConsignmentAmount();
        const docketCharge = parseFloat(watch('docketCharge')) || 0;
        const odaCharge = parseFloat(watch('odaCharge')) || 0;
        const otherCharge = parseFloat(watch('otherCharge')) || 0;
        // Note: Taxable base is basically the total for Non-GST
        return consignmentAmount + docketCharge + odaCharge + otherCharge;
    };

    const getDaysInclusive = (from, to) => {
        try {
            const d1 = new Date(from);
            const d2 = new Date(to);
            const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
            return (isNaN(diff) ? 0 : diff) + 1;
        } catch {
            return 0;
        }
    };

    const handlePreview = () => {
        const currentInvoiceNo = watch('invoiceNo');
        const data = {
            invoiceNo: currentInvoiceNo,
            customerId: watch('customerId'),
            invoiceDate: watch('invoiceDate'),
            fromDate: watch('fromDate'),
            toDate: watch('toDate'),
            fuelCharge: parseFloat(watch('fuelCharge') || 0),
            royaltyCharge: parseFloat(watch('royaltyCharge') || 0),
            docketCharge: parseFloat(watch('docketCharge') || 0),
            odaCharge: parseFloat(watch('odaCharge') || 0),
            otherCharge: parseFloat(watch('otherCharge') || 0),
            gstPercent: 0 // Always 0 for Non-GST
        };

        if (selectedBookings.length === 0) {
            toast.error('Please select at least one booking');
            return;
        }

        const selectedCustomer = customers.find(c => c.id === data.customerId);
        if (!selectedCustomer) {
            toast.error('Please select a customer');
            return;
        }

        const selectedBookingsData = filteredBookings.filter(b => selectedBookings.includes(b.id));
        const consignmentAmount = calculateConsignmentAmount();

        const docketCharge = parseFloat(data.docketCharge) || 0;
        const odaCharge = parseFloat(data.odaCharge) || 0;
        const otherCharge = parseFloat(data.otherCharge) || 0;
        const fuelChargePercent = sanitizePercentInput(data.fuelCharge);
        const royaltyChargePercent = sanitizePercentInput(data.royaltyCharge);

        const { baseRupees, fuelAmountRupees, royaltyAmountRupees, taxableValueRupees } = computeInvoiceCharges({
            consignmentAmount,
            odaCharges: odaCharge,
            docketCharges: docketCharge,
            otherCharges: otherCharge,
            fuelPercent: fuelChargePercent,
            royaltyPercent: royaltyChargePercent,
        });

        const totalBeforeGST = taxableValueRupees;

        // NON-GST specific logic - use IGST with 0% to avoid DB constraint violations
        // The display logic in InvoiceLayout will hide the rows based on the 'NG-' prefix
        const gstCalculation = {
            gstType: 'IGST',
            cgstPercent: 0,
            sgstPercent: 0,
            igstPercent: 0,
            cgstAmount: 0,
            sgstAmount: 0,
            igstAmount: 0,
            totalGST: 0,
            gstPercent: 0
        };

        const grandTotal = totalBeforeGST; // No GST added

        // Ensure invoice number is numeric and padded
        const match = ((data.invoiceNo || nextInvoiceNo) + '').match(/(\d+)$/);
        const digits = match ? match[0] : '001';
        const numericPart = digits.length < 3 ? digits.padStart(3, '0') : digits;
        const ng_prefix = 'NG-';
        const invoiceNoPadded = `${ng_prefix}${numericPart}`;

        const invoiceData = {
            number: invoiceNoPadded,
            date: data.invoiceDate,
            fromDate: data.fromDate,
            toDate: data.toDate,
            customer: {
                name: selectedCustomer.customer_name,
                address: selectedCustomer.address,
                gst: selectedCustomer.gst_no
            },
            days: getDaysInclusive(data.fromDate, data.toDate),
            consignment: selectedBookingsData.length,
            weight: selectedBookingsData.reduce((sum, booking) => sum + parseFloat(booking.weight || 0), 0).toFixed(2),
            amount: consignmentAmount.toFixed(2),
            items: selectedBookingsData.map((booking) => ({
                date: booking.date,
                cnote: booking.awb_no || booking.awbNo,
                receiver: booking.receiver_name || booking.receiverName,
                destination: booking.receiver_address || booking.receiverAddress,
                mode: booking.mode,
                weight: booking.weight,
                qty: booking.quantity,
                type: booking.type,
                amount: parseFloat(booking.amount || 0).toFixed(2),
                other: parseFloat(booking.other_charge || 0).toFixed(2)
            })),
            subtotal: totalBeforeGST.toFixed(2),
            fuelPercent: fuelChargePercent,
            royaltyPercent: royaltyChargePercent,
            fuelCharge: fuelAmountRupees.toFixed(2),
            royaltyCharge: royaltyAmountRupees.toFixed(2),
            docketCharge: docketCharge.toFixed(2),
            odaCharge: odaCharge.toFixed(2),
            otherCharge: otherCharge.toFixed(2),
            gstType: 'IGST', // Changed back to IGST for DB compatibility
            cgstPercent: 0,
            cgstAmount: 0,
            sgstPercent: 0,
            sgstAmount: 0,
            igstPercent: 0,
            igstAmount: 0,
            igst: "0.00",
            total: grandTotal.toFixed(2)
        };

        setPreviewInvoice(invoiceData);
        setShowPreview(true);

        // Persist exact data for generate
        const safeInvoiceDate = data.invoiceDate || new Date().toISOString().split('T')[0];
        const dbInvoiceData = {
            ...data,
            invoiceNo: invoiceNoPadded, // Ensure DB saves with prefix
            customerId: data.customerId,
            invoiceDate: safeInvoiceDate,
            fromDate: data.fromDate,
            toDate: data.toDate,
            subtotal: totalBeforeGST,
            fuelPercent: fuelChargePercent,
            royaltyPercent: royaltyChargePercent,
            fuelCharge: fuelAmountRupees,
            royaltyCharge: royaltyAmountRupees,
            docketCharge,
            odaCharge,
            otherCharge,
            totalCharges: fuelAmountRupees + royaltyAmountRupees + docketCharge + odaCharge + otherCharge,
            totalBeforeGST,
            gstCalculation,
            grandTotal,
        };
        setLastPreviewPayload({ invoiceData: dbInvoiceData, selectedBookingsData });
    };

    const onSubmit = async (data) => {
        if (selectedBookings.length === 0) {
            toast.error('Please select at least one booking');
            return;
        }

        setIsLoading(true);
        try {
            if (lastPreviewPayload) {
                let { invoiceData, selectedBookingsData } = lastPreviewPayload;
                // Double check to ensure we are sending the prefixed invoice number if it wasn't set correctly
                const ng_prefix = 'NG-';
                if (invoiceData.invoiceNo && !invoiceData.invoiceNo.startsWith(ng_prefix)) {
                    const match = (invoiceData.invoiceNo + '').match(/(\d+)$/);
                    const digits = match ? match[0] : '001';
                    const num = digits.length < 3 ? digits.padStart(3, '0') : digits;
                    invoiceData = { ...invoiceData, invoiceNo: `${ng_prefix}${num}` };
                }

                if (isEditMode && editInvoiceId) {
                    await updateInvoice(user.id, editInvoiceId, invoiceData, selectedBookingsData);
                    toast.success(`Non-GST Invoice ${invoiceData.invoiceNo} updated successfully!`);
                    setLastPreviewPayload(null);
                    setIsLoading(false);
                    navigate('/invoices');
                    return;
                }

                await createInvoice(user.id, invoiceData, selectedBookingsData);
                toast.success(`Non-GST Invoice ${invoiceData.invoiceNo} generated successfully!`);
                setLastPreviewPayload(null);

                // Refresh next number
                const nextNo = await getNextInvoiceNo(user.id, true);
                const numPart = nextNo.replace('NG-', '');
                setNextInvoiceNo(numPart);
                try {
                    setValue('invoiceNo', numPart, { shouldValidate: true });
                } catch (e) { }
                setIsLoading(false);
                return;
            }

            const selectedCustomer = customers.find(c => c.id === data.customerId);
            const selectedBookingsData = filteredBookings.filter(b => selectedBookings.includes(b.id));

            const safeInvoiceDate = data.invoiceDate || new Date().toISOString().split('T')[0];
            const safeFromDate = data.fromDate;
            const safeToDate = data.toDate;
            if (!selectedCustomer || !safeFromDate || !safeToDate) {
                toast.error('Please select customer and date range');
                setIsLoading(false);
                return;
            }

            const consignmentAmount = calculateConsignmentAmount();
            const docketCharge = parseFloat(data.docketCharge) || 0;
            const odaCharge = parseFloat(data.odaCharge) || 0;
            const otherCharge = parseFloat(data.otherCharge) || 0;
            const fuelChargePercent = sanitizePercentInput(data.fuelCharge);
            const royaltyChargePercent = sanitizePercentInput(data.royaltyCharge);

            const { baseRupees, fuelAmountRupees, royaltyAmountRupees, taxableValueRupees } = computeInvoiceCharges({
                consignmentAmount,
                odaCharges: odaCharge,
                docketCharges: docketCharge,
                otherCharges: otherCharge,
                fuelPercent: fuelChargePercent,
                royaltyPercent: royaltyChargePercent,
            });

            const totalBeforeGST = taxableValueRupees;

            const gstCalculation = {
                gstType: 'IGST', // Changed back to IGST for DB compatibility
                cgstPercent: 0,
                sgstPercent: 0,
                igstPercent: 0,
                cgstAmount: 0,
                sgstAmount: 0,
                igstAmount: 0,
                totalGST: 0,
                gstPercent: 0
            };

            const grandTotal = totalBeforeGST;

            const match = ((data.invoiceNo || nextInvoiceNo) + '').match(/(\d+)$/);
            const digits = match ? match[0] : '001';
            const numericPart = digits.length < 3 ? digits.padStart(3, '0') : digits;
            const ng_prefix = 'NG-';
            const invoiceNoPadded = `${ng_prefix}${numericPart}`;

            const invoiceData = {
                ...data,
                invoiceNo: invoiceNoPadded,
                customerName: selectedCustomer?.customer_name,
                invoiceDate: safeInvoiceDate,
                fromDate: safeFromDate,
                toDate: safeToDate,
                subtotal: totalBeforeGST,
                fuelPercent: fuelChargePercent, // Persist percentages
                royaltyPercent: royaltyChargePercent,
                fuelCharge: fuelAmountRupees,
                royaltyCharge: royaltyAmountRupees,
                docketCharge,
                odaCharge,
                otherCharge,
                totalCharges: fuelAmountRupees + royaltyAmountRupees + docketCharge + odaCharge + otherCharge,
                totalBeforeGST,
                gstCalculation,
                grandTotal,
                selectedBookings: selectedBookingsData,
                createdAt: new Date().toISOString()
            };

            if (isEditMode && editInvoiceId) {
                await updateInvoice(user.id, editInvoiceId, invoiceData, selectedBookingsData);
                toast.success(`Invoice ${invoiceData.invoiceNo} updated successfully!`);
                navigate('/invoices');
                return;
            } else {
                await createInvoice(user.id, invoiceData, selectedBookingsData);
                toast.success(`Non-GST Invoice ${invoiceData.invoiceNo} generated successfully!`);
            }

            if (!isEditMode) {
                setSelectedBookings([]);
                setValue('customerId', '');
                setValue('fromDate', '');
                setValue('toDate', '');
                setValue('fuelCharge', '');
                setValue('royaltyCharge', '');
                setValue('docketCharge', '');
                setValue('odaCharge', '');
                setValue('otherCharge', '');
                // gstPercent is not even in form

                const nextNo = await getNextInvoiceNo(user.id, true);
                const numPart = nextNo.replace('NG-', '');
                setNextInvoiceNo(numPart);
                setValue('invoiceNo', numPart);
            }

        } catch (error) {
            console.error(error);
            toast.error(`Failed to generate invoice: ${error.message || error}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEditMode ? 'Edit Non-GST Invoice' : 'Non-GST Invoice Generator'}
                    </h1>
                    <p className="text-gray-600">
                        Generate invoices without GST calculations.
                    </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                <FileText className="h-5 w-5 mr-2" />
                                Invoice Details
                            </h3>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Invoice Number
                                    </label>
                                    <div className="flex rounded-md shadow-sm">
                                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 font-medium sm:text-sm">
                                            NG-
                                        </span>
                                        <input
                                            type="text"
                                            readOnly
                                            {...register('invoiceNo')}
                                            className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-none rounded-r-md focus:outline-none focus:ring-0 font-mono font-bold text-primary-600"
                                            defaultValue={nextInvoiceNo}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Invoice Date *
                                    </label>
                                    <input
                                        type="date"
                                        {...register('invoiceDate', { required: 'Invoice date is required' })}
                                        onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        defaultValue={new Date().toISOString().split('T')[0]}
                                    />
                                    {errors.invoiceDate && (
                                        <p className="mt-1 text-sm text-red-600">{errors.invoiceDate.message}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <CustomerAutocomplete
                                    label="Customer *"
                                    placeholder="Type customer ID"
                                    value={watch('customerId')}
                                    valueLabel={customers.find(c => c.id === watch('customerId'))?.customer_name || ''}
                                    onChange={(c) => {
                                        setValue('customerId', c.id, { shouldValidate: true });
                                        if (c.fuel_charge !== undefined && c.fuel_charge !== null) {
                                            setValue('fuelCharge', c.fuel_charge);
                                        }
                                        if (c.royalty_charge !== undefined && c.royalty_charge !== null) {
                                            setValue('royaltyCharge', c.royalty_charge);
                                        }
                                    }}
                                    error={errors.customerId?.message}
                                />
                                <input type="hidden" {...register('customerId', { required: 'Please select a customer' })} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        From Date *
                                    </label>
                                    <input
                                        type="date"
                                        {...register('fromDate', { required: 'From date is required' })}
                                        onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                    {errors.fromDate && (
                                        <p className="mt-1 text-sm text-red-600">{errors.fromDate.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        To Date *
                                    </label>
                                    <input
                                        type="date"
                                        {...register('toDate', { required: 'To date is required' })}
                                        onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    />
                                    {errors.toDate && (
                                        <p className="mt-1 text-sm text-red-600">{errors.toDate.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {filteredBookings.length > 0 && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                        <Package className="h-5 w-5 mr-2" />
                                        Available Bookings ({filteredBookings.length})
                                    </h3>
                                    <div className="flex space-x-2">
                                        <button
                                            type="button"
                                            onClick={selectAllBookings}
                                            className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={clearSelection}
                                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {filteredBookings.map((booking) => (
                                        <div
                                            key={booking.id}
                                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedBookings.includes(booking.id)
                                                ? 'border-primary-500 bg-primary-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            onClick={() => toggleBookingSelection(booking.id)}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <span className="font-medium">AWB #{booking.awb_no || booking.awbNo}</span>
                                                    <span className="ml-2 text-sm text-gray-500">
                                                        {(booking.receiver_name || booking.receiverName)} - {booking.mode}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="font-medium">₹{parseFloat(booking.amount).toFixed(2)}</span>
                                                    <div className="text-sm text-gray-500">{booking.date}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                <Calculator className="h-5 w-5 mr-2" />
                                Additional Charges
                            </h3>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Fuel Charge (%)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('fuelCharge', {
                                            min: { value: 0, message: 'Fuel % cannot be negative' },
                                            max: { value: 100, message: 'Fuel % cannot exceed 100' }
                                        })}
                                        min={0}
                                        max={100}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        SURCHARGE (%)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('royaltyCharge', {
                                            min: { value: 0, message: 'Surcharge % cannot be negative' },
                                            max: { value: 100, message: 'Surcharge % cannot exceed 100' }
                                        })}
                                        min={0}
                                        max={100}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Docket Charge (₹)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('docketCharge', {
                                            min: { value: 0, message: 'Docket charge cannot be negative' },
                                            max: { value: 99999999.99, message: 'Docket charge too large' }
                                        })}
                                        min={0}
                                        max={99999999.99}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        ODA Charge (₹)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('odaCharge', {
                                            min: { value: 0, message: 'ODA charge cannot be negative' },
                                            max: { value: 99999999.99, message: 'ODA charge too large' }
                                        })}
                                        min={0}
                                        max={99999999.99}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Other Charge (₹)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('otherCharge', {
                                            min: { value: 0, message: 'Other charge cannot be negative' },
                                            max: { value: 99999999.99, message: 'Other charge too large' }
                                        })}
                                        min={0}
                                        max={99999999.99}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            {selectedBookings.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Summary</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Selected Bookings:</span>
                                            <span className="ml-2 font-medium">{selectedBookings.length}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Consignment Amount:</span>
                                            <span className="ml-2 font-medium">₹{calculateConsignmentAmount().toFixed(2)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Additional Charges:</span>
                                            <span className="ml-2 font-medium">₹{(
                                                (parseFloat(watch('fuelCharge') || 0) +
                                                    parseFloat(watch('royaltyCharge') || 0) +
                                                    parseFloat(watch('docketCharge') || 0) +
                                                    parseFloat(watch('odaCharge') || 0) +
                                                    parseFloat(watch('otherCharge') || 0))
                                            ).toFixed(2)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Total (No GST):</span>
                                            <span className="ml-2 font-medium font-bold">₹{calculateTaxableBase().toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={handlePreview}
                            disabled={isLoading || selectedBookings.length === 0}
                            className="flex items-center px-6 py-3 bg-white text-primary-600 border border-primary-600 font-medium rounded-md hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Eye className="h-5 w-5 mr-2" />
                            Preview Invoice
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || selectedBookings.length === 0}
                            className="flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isEditMode ? <Save className="h-5 w-5 mr-2" /> : <Download className="h-5 w-5 mr-2" />}
                            {isLoading
                                ? (isEditMode ? 'Updating...' : 'Generating...')
                                : (isEditMode ? 'Update Invoice' : 'Generate Invoice')}
                        </button>
                    </div>
                </form>

                <footer className="mt-8 border-t border-gray-200">
                    <div className="py-4 text-center text-xs text-gray-500">
                        Proprietary Billing Infrastructure
                    </div>
                </footer>
            </div>

            {showPreview && previewInvoice && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-screen overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold">Non-GST Invoice Preview</h2>
                                <button
                                    onClick={() => setShowPreview(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div ref={printComponentRef}>
                                    <NonGstInvoiceLayout invoice={previewInvoice} profile={franchiseProfile} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NonGstInvoiceGenerator;
